import { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Text,
  useToast,
} from '@chakra-ui/react';
import { FiDownload, FiTrash2 } from 'react-icons/fi';
import { EntregaHistorico, excluirEntrega, obterCanhoto } from '../../service/entregas';
import { dataIsoParaBr } from '../../helpers/dataBr';

const Etapa = ({ rotulo, data, hora }: { rotulo: string; data?: string; hora?: string }) => (
  <Box minW={0}>
    <Text fontSize="10px" color="gray.400" letterSpacing="wider">
      {rotulo}
    </Text>
    <Text fontSize="sm" fontWeight="medium">
      {data ? `${dataIsoParaBr(data)}${hora ? ` às ${hora}` : ''}` : '—'}
    </Text>
  </Box>
);

interface Props {
  entrega: EntregaHistorico;
  onClose: () => void;
  /** Depois de excluir: a lista recarrega sem a entrega. */
  onExcluido: () => void;
}

export const CanhotoModal = ({ entrega, onClose, onExcluido }: Props) => {
  const toast = useToast();
  const [urlImagem, setUrlImagem] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false);
  const [excluindo, setExcluindo] = useState(false);

  // A imagem vem por uma rota autenticada, então é baixada como blob e vira um
  // object URL. Ele precisa ser revogado ao fechar o modal — senão o arquivo
  // fica preso na memória da aba a cada canhoto aberto.
  useEffect(() => {
    let ativo = true;
    let objectUrl: string | null = null;

    setCarregando(true);
    setErro(null);
    setUrlImagem(null);

    obterCanhoto(entrega.entregaId)
      .then((url) => {
        objectUrl = url;
        if (ativo) setUrlImagem(url);
        else URL.revokeObjectURL(url);
      })
      .catch((falha: any) => {
        if (!ativo) return;
        // O arquivo mora no disco do servidor da API; um 404 aqui costuma ser
        // canhoto que existe no banco mas sumiu do disco (ver obterCanhoto).
        setErro(
          falha?.response?.status === 404
            ? 'O arquivo do canhoto não está mais no servidor.'
            : 'Não foi possível carregar o canhoto.'
        );
      })
      .finally(() => {
        if (ativo) setCarregando(false);
      });

    return () => {
      ativo = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [entrega.entregaId]);

  const nomeArquivo = `canhoto-${entrega.notaErp || entrega.pedidoErp || entrega.entregaId}.jpg`;

  // Excluir apaga a ENTREGA (DELETE /entregas/:id), não só o arquivo: é assim
  // que a nota volta a aceitar outro canhoto. Só removendo o arquivo, o
  // /coletar continuaria recusando a nota como "já entregue".
  const excluir = async () => {
    setExcluindo(true);
    try {
      await excluirEntrega(entrega.entregaId);
      toast({
        title: 'Canhoto excluído',
        description: entrega.notaErp ? `A NF ${entrega.notaErp} pode receber outro canhoto.` : undefined,
        status: 'success',
        duration: 4000,
        isClosable: true,
        position: 'top-right',
      });
      onExcluido();
    } catch (falha: any) {
      setExcluindo(false);
      toast({
        title: 'Não foi possível excluir o canhoto',
        description: falha?.response?.data?.message || 'Tente novamente daqui alguns minutos.',
        status: 'error',
        duration: 6000,
        isClosable: true,
        position: 'top-right',
      });
    }
  };

  return (
    <Modal isOpen onClose={onClose} size="2xl" scrollBehavior="inside" isCentered>
      <ModalOverlay />
      <ModalContent bg="gray.900" color="gray.50" borderRadius="xl" overflow="hidden">
        <ModalHeader px={{ base: 5, md: 6 }} pt={5} pb={4}>
          <Text fontSize="xs" color="gray.400" letterSpacing="wider">
            CANHOTO ASSINADO
          </Text>
          <Text mt={1} pr={10} fontSize="lg" lineHeight="shorter">
            {entrega.cliente || 'Destino não informado'}
          </Text>
          <Flex mt={3} gap={2} wrap="wrap">
            {entrega.notaErp && (
              <Badge colorScheme="orange" fontSize="10px">
                NF {entrega.notaErp}
              </Badge>
            )}
            {entrega.pedidoErp && (
              <Badge colorScheme="blue" fontSize="10px">
                Pedido {entrega.pedidoErp}
              </Badge>
            )}
            {entrega.empresaNome && (
              <Badge colorScheme="purple" fontSize="10px">
                {entrega.empresaNome}
              </Badge>
            )}
            {entrega.manual && (
              <Badge colorScheme="gray" fontSize="10px">
                Anexo manual
              </Badge>
            )}
          </Flex>
        </ModalHeader>
        <ModalCloseButton isDisabled={excluindo} />

        <ModalBody px={{ base: 5, md: 6 }} pb={6}>
          {/* No anexo manual não houve viagem: a coleta é do mesmo instante do
              anexo e não existe saída para rota. Quem aparece como "motorista"
              é a pessoa que anexou pela web. */}
          <SimpleGrid columns={{ base: 2, md: entrega.manual ? 2 : 4 }} spacing={4} mb={5}>
            <Box minW={0}>
              <Text fontSize="10px" color="gray.400" letterSpacing="wider">
                {entrega.manual ? 'ANEXADO POR' : 'MOTORISTA'}
              </Text>
              <Text fontSize="sm" fontWeight="medium" isTruncated>
                {entrega.motorista || '—'}
              </Text>
            </Box>
            {!entrega.manual && (
              <>
                <Etapa rotulo="COLETADO" data={entrega.coleta?.data} hora={entrega.coleta?.hora} />
                <Etapa rotulo="SAIU PARA ROTA" data={entrega.inicioRota?.data} hora={entrega.inicioRota?.hora} />
              </>
            )}
            <Etapa
              rotulo={entrega.manual ? 'ANEXADO EM' : 'ENTREGUE'}
              data={entrega.entrega?.data}
              hora={entrega.entrega?.hora}
            />
          </SimpleGrid>

          <Flex
            align="center"
            justify="center"
            minH="320px"
            bg="gray.800"
            borderRadius="lg"
            borderWidth="1px"
            borderColor="gray.700"
            p={3}
          >
            {carregando ? (
              <Spinner color="orange.200" size="lg" />
            ) : erro ? (
              <Text color="red.300" fontSize="sm" textAlign="center">
                {erro}
              </Text>
            ) : (
              urlImagem && (
                <Image
                  src={urlImagem}
                  alt={`Canhoto da entrega para ${entrega.cliente}`}
                  maxH="60vh"
                  borderRadius="md"
                />
              )
            )}
          </Flex>

          {confirmandoExclusao ? (
            <Flex
              mt={4}
              p={3}
              align={{ base: 'stretch', md: 'center' }}
              direction={{ base: 'column', md: 'row' }}
              justify="space-between"
              bg="gray.800"
              borderWidth="1px"
              borderColor="red.400"
              borderRadius="md"
            >
              <Text fontSize="sm" mb={{ base: 3, md: 0 }} mr={{ md: 4 }}>
                Excluir este canhoto?{' '}
                <Text as="span" color="gray.400">
                  A nota volta a ficar sem canhoto e pode receber outro.
                </Text>
              </Text>
              <HStack spacing={2} justify="flex-end" flexShrink={0}>
                <Button size="sm" variant="ghost" isDisabled={excluindo} onClick={() => setConfirmandoExclusao(false)}>
                  Cancelar
                </Button>
                <Button size="sm" colorScheme="red" isLoading={excluindo} onClick={excluir}>
                  Excluir
                </Button>
              </HStack>
            </Flex>
          ) : (
            <Flex justify="space-between" align="center" mt={4}>
              {/* Excluir só no anexo manual: a entrega do motorista tem trajeto
                  e horários de rota que a web não consegue refazer. */}
              {entrega.manual ? (
                <Button
                  size="sm"
                  variant="ghost"
                  colorScheme="red"
                  leftIcon={<FiTrash2 />}
                  onClick={() => setConfirmandoExclusao(true)}
                >
                  Excluir
                </Button>
              ) : (
                <Box />
              )}
              {urlImagem && (
                <Button
                  as="a"
                  href={urlImagem}
                  download={nomeArquivo}
                  size="sm"
                  colorScheme="orange"
                  leftIcon={<FiDownload />}
                >
                  Baixar
                </Button>
              )}
            </Flex>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
